import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import ClientCreateModal from '@/components/modals/ClientCreateModal';
import {
  SearchBar,
  AppliedFilters,
  FilterDropdown,
} from '@/components/filters';
import {
  createClientFilterConfig,
  CLIENT_FILTER_KEYS,
} from '@/components/client/utils/clientFilters.utils';
import { useClientStore } from '@core/client/useClientStore';
import {
  Address,
  Maybe,
  ClientStatus,
  Client,
} from '@core/generated/gql-types';
import { formatChargeTypeLabel } from '@/utils/client.constants';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { Skeleton, SkeletonTable } from '@/components/shared/Skeleton';
import { Button } from '@/components';
import { useColumnResize } from '@/components/job/useColumnResize';
import { ResizeHandle } from '@/components/shared/ResizeHandle';
import { useTenantId } from '@/contexts/TenantContext';

type SortField =
  | 'status'
  | 'name'
  | 'address'
  | 'contactNumber'
  | 'email'
  | 'admin'
  | 'chargeType'
  | null;
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 20;

const Clients = () => {
  const { getTerm } = useTemplateStore();
  const { clients, fetchClientsForTenant } = useClientStore();
  const tenantId = useTenantId();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasFetchedClients, setHasFetchedClients] = useState(false);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(CLIENT_FILTER_KEYS.map((key) => [key, []])),
  );

  const clientFilterConfig = useMemo(() => createClientFilterConfig(), []);
  const setFilterByKey = useCallback((key: string) => {
    return (values: string[]) => {
      setFilters((prev) => ({ ...prev, [key]: values }));
    };
  }, []);

  const fetchClients = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);
    try {
      await fetchClientsForTenant(tenantId || '', undefined, undefined, true);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, fetchClientsForTenant]);

  useEffect(() => {
    if (!hasFetchedClients && tenantId) {
      fetchClients()
        .then(() => setHasFetchedClients(true))
        .catch((error) => {
          console.error('Error fetching clients:', error);
        });
    }
  }, [tenantId, hasFetchedClients, fetchClients]);

  const buildAddressString = (address?: Maybe<Address>) => {
    if (!address) {
      return '';
    }
    return `${address.addressLine1}${address.addressLine2 ? ` ${address.addressLine2}` : ''} ${address.suburb}, ${address.state} ${address.postcode}`;
  };

  const handleClientClick = (clientId: string) => {
    window.open(`/clients/${clientId}`, '_blank', 'noopener,noreferrer');
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↑↓';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusBadgeClass = (status?: Maybe<ClientStatus>) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Column configuration
  const columns = [
    {
      key: 'name',
      title: 'Company Name',
      width: 200,
      sortable: true,
      renderCell: (client: Client) => (
        <div
          className="truncate text-sm text-gray-900"
          title={client.name || undefined}
        >
          {client.name}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'status',
      title: 'Status',
      width: 120,
      sortable: true,
      renderCell: (client: Client) => (
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(client.status)}`}
        >
          {client.status || 'Unknown'}
        </span>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
    {
      key: 'address',
      title: 'Address',
      width: 400,
      sortable: true,
      renderCell: (client: Client) => {
        const address = buildAddressString(client?.address);
        return (
          <div className="truncate text-sm text-gray-900" title={address}>
            {address}
          </div>
        );
      },
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden',
    },
    {
      key: 'chargeType',
      title: 'Charge Type',
      width: 100,
      sortable: true,
      renderCell: (client: Client) => (
        <div className="text-sm text-gray-900">
          {client.chargeType
            ? formatChargeTypeLabel(client.chargeType)
            : 'Unknown'}
        </div>
      ),
      cellClassName:
        'border-t border-gray-200 px-4 py-3 align-middle overflow-hidden whitespace-nowrap',
    },
  ];

  // Build defaultWidths from column config
  const defaultWidths = columns.reduce(
    (acc, col) => {
      acc[col.key] = col.width;
      return acc;
    },
    {} as Record<string, number>,
  );

  const { columnWidths, handleMouseDown } = useColumnResize({
    defaultWidths,
    minWidth: 80,
  });

  const tableRef = useRef<HTMLTableElement>(null);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || isLoading) return;
    if (newPage > totalPages) return;

    setCurrentPage(newPage);
  };

  const sortedClients = [...(clients || [])].sort((a, b) => {
    if (!sortField) return 0;

    const direction = sortDirection === 'asc' ? 1 : -1;
    let addressA: string;
    let addressB: string;

    switch (sortField) {
      case 'status':
        return direction * (a.status || '').localeCompare(b.status || '');
      case 'name':
        return direction * (a.name || '').localeCompare(b.name || '');
      case 'address':
        addressA = buildAddressString(a.address);
        addressB = buildAddressString(b.address);
        return direction * addressA.localeCompare(addressB);
      case 'chargeType':
        return (
          direction * (a.chargeType || '').localeCompare(b.chargeType || '')
        );
      default:
        return 0;
    }
  });

  const filteredAndSortedClients = useMemo(() => {
    return sortedClients.filter((client) => {
      const name = client.name?.toLowerCase() || '';
      const address = buildAddressString(client.address).toLowerCase();
      const status = client.status?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        name.includes(query) ||
        address.includes(query) ||
        status.includes(query);

      const matchesAllFilters = clientFilterConfig.every((config) => {
        const filterValues = filters[config.key];
        if (!filterValues?.length) return true;

        const clientValues = config.getValueFromItem(client);
        return clientValues.some((v) => filterValues.includes(v));
      });

      return matchesSearch && matchesAllFilters;
    });
  }, [sortedClients, searchQuery, filters, clientFilterConfig]);

  const totalClientsCount = filteredAndSortedClients.length;
  const totalPages = Math.max(1, Math.ceil(totalClientsCount / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = useMemo(() => {
    return (currentPage - 1) * ITEMS_PER_PAGE + 1;
  }, [currentPage]);

  const endIndex = useMemo(() => {
    return Math.min(startIndex + ITEMS_PER_PAGE - 1, totalClientsCount);
  }, [startIndex, totalClientsCount]);

  const filteredClients = useMemo(
    () => filteredAndSortedClients.slice(startIndex - 1, endIndex),
    [filteredAndSortedClients, startIndex, endIndex],
  );

  // Show skeleton loading while data is being fetched initially
  if (!hasFetchedClients && isLoading) {
    return (
      <div className="flex h-full flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <Skeleton height="h-8" width="w-24" />
          <Skeleton height="h-10" width="w-36" />
        </div>

        <div className="mb-2 flex items-center gap-2">
          <Skeleton height="h-10" width="w-64" />
          <Skeleton height="h-10" width="w-32" />
        </div>

        <SkeletonTable rows={8} columns={3} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">
          {getTerm(TemplateTerm.CLIENT, true)}
        </h2>
        <Button
          variant="primary"
          className="bg-accent-900 hover:bg-accent-700 cursor-pointer px-3 py-3"
          onClick={() => setIsModalOpen(true)}
        >
          + Add new {getTerm(TemplateTerm.CLIENT, false)}
        </Button>
      </div>
      <div className="mb-2 flex items-center gap-2">
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        {clientFilterConfig.map((config) => (
          <FilterDropdown
            key={config.key}
            title={config.title}
            data={filteredAndSortedClients ?? []}
            getOptions={config.getOptions}
            selectedFilters={filters[config.key] ?? []}
            setSelectedFilters={setFilterByKey(config.key)}
          />
        ))}
      </div>
      <AppliedFilters
        filterGroups={clientFilterConfig.map(({ key, title }) => ({
          label: title,
          values: filters[key] ?? [],
          setValues: setFilterByKey(key),
        }))}
      />
      <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
        <table
          ref={tableRef}
          className="w-full border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.key}
                style={{ width: `${columnWidths[col.key]}px` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() =>
                    col.sortable && handleSort(col.key as SortField)
                  }
                  className={`relative px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase hover:bg-gray-50 ${
                    col.sortable ? 'cursor-pointer' : ''
                  }`}
                  style={{ width: `${columnWidths[col.key]}px` }}
                >
                  <div className="flex items-center">
                    {col.title}
                    {col.sortable && (
                      <span className="ml-2 text-gray-400">
                        {getSortIcon(col.key as SortField)}
                      </span>
                    )}
                  </div>
                  <ResizeHandle
                    column={col.key}
                    onMouseDown={handleMouseDown}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? // Show skeleton rows for pagination loading
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {columns.map((col, colIndex) => (
                      <td key={col.key} className={col.cellClassName}>
                        {colIndex === 0 && (
                          <Skeleton
                            height="h-6"
                            width="w-16"
                            rounded-sm="full"
                          />
                        )}
                        {colIndex === 1 && (
                          <Skeleton height="h-4" width="w-32" />
                        )}
                        {colIndex === 2 && (
                          <Skeleton height="h-4" width="w-48" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              : filteredClients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => handleClientClick(client.id)}
                    className="cursor-pointer transition-colors hover:bg-gray-100"
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={col.cellClassName}>
                        {col.renderCell(client)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {totalClientsCount === 0 ? 0 : startIndex} to {endIndex} of{' '}
          {totalClientsCount} results
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={
              currentPage >= totalPages || totalPages === 0 || isLoading
            }
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <ClientCreateModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </div>
  );
};

export default Clients;
